import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Tag } from "lucide-react";
import { PortalLayout } from "../../components/PortalLayout";
import { api, formatApiError } from "../../lib/api";
import { PLANS, WHY_MEMBER, EVERY_MEMBERSHIP } from "../../lib/membership";

export default function Partnership() {
  const [busyKey, setBusyKey] = useState(null);
  const [promo, setPromo] = useState("");

  const subscribe = async (lookup_key) => {
    setBusyKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin, promo_code: promo.trim() });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusyKey(null);
    }
  };

  return (
    <PortalLayout>
      <div data-testid="partnership-page">
        <p className="font-accent text-lg text-[#7C8514]">The Ditch Careers Recruitment Membership</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Your Recruitment Team. Without the Recruitment Overheads.</h1>
        <div className="text-sm text-[#51637a] mt-4 max-w-2xl leading-relaxed space-y-3">
          <p><span className="font-medium text-[#0d2f4d]">Recruitment shouldn't be reactive.</span> The best businesses don't wait until someone resigns to think about hiring. They build talent pipelines, plan ahead, and partner with experts who understand their business. That's exactly what we do.</p>
          <p>The Ditch Careers Recruitment Membership provides your business with ongoing access to an experienced recruitment team for one predictable monthly investment. No unexpected recruitment invoices. No scrambling to fill vacancies. Just a trusted recruitment partner invested in your long-term success.</p>
        </div>

        <div className="ditch-card p-6 mt-8" data-testid="why-member-card">
          <h2 className="font-heading font-bold text-[#0d2f4d]">Why become a member?</h2>
          <p className="text-sm text-[#51637a] mt-2">Unlike traditional agencies that charge a large fee every time you hire, our membership gives you continuous recruitment support throughout the year. As a member you'll receive:</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {WHY_MEMBER.map((w) => (
              <p key={w} className="flex items-start gap-2 text-sm text-[#122c47]"><Check size={15} className="text-[#7C8514] mt-0.5 shrink-0" /> {w}</p>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {PLANS.map((p) => (
            <div key={p.key} className={`ditch-card p-7 flex flex-col ${p.popular ? "border-[#C1CC24] bg-[#C1CC24]/5" : ""}`} data-testid={`plan-card-${p.key}`}>
              {p.popular && <span className="self-start rounded-full bg-[#C1CC24] text-[#0d2f4d] text-xs font-semibold px-3 py-1 mb-3">Most popular</span>}
              <h2 className="font-heading font-bold text-[#0d2f4d] text-xl">{p.name} <span className="font-accent font-medium">Membership</span></h2>
              <p className="mt-3"><span className="font-heading text-3xl font-extrabold text-[#0d2f4d]">{p.price}</span><span className="text-sm text-[#51637a]"> + GST /month</span></p>
              <p className="text-sm text-[#51637a] mt-3 leading-relaxed">{p.tagline}</p>
              <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#51637a] mt-5">{p.lead}</p>
              <ul className="mt-3 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#122c47]">
                    <Check size={15} className="text-[#7C8514] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => subscribe(p.key)} disabled={busyKey !== null}
                data-testid={`subscribe-${p.key}`}
                className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm py-3.5 transition-colors disabled:opacity-60 ${
                  p.popular ? "bg-[#C1CC24] text-[#0d2f4d] hover:bg-[#aeb81f]" : "bg-[#0d2f4d] text-white hover:bg-[#1c4d78]"}`}>
                {busyKey === p.key && <Loader2 size={15} className="animate-spin" />}
                {busyKey === p.key ? "Redirecting..." : "Become a member"}
              </button>
            </div>
          ))}
        </div>

        <div className="ditch-card p-6 mt-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="promo-code-card">
          <div className="flex-1">
            <h2 className="font-heading font-bold text-[#0d2f4d] flex items-center gap-2"><Tag size={16} className="text-[#7C8514]" /> Have a discount code?</h2>
            <p className="text-sm text-[#51637a] mt-1">Enter it here and we'll apply it automatically at checkout.</p>
          </div>
          <input
            value={promo}
            onChange={(e) => setPromo(e.target.value)}
            placeholder="Enter promo code"
            data-testid="promo-code-input"
            className="w-full sm:w-64 rounded-lg border border-[#e3e0d3] bg-white px-4 py-3 text-sm text-[#0d2f4d] uppercase tracking-wide placeholder:normal-case placeholder:tracking-normal focus:border-[#0d2f4d] focus:outline-none"
          />
        </div>

        <div className="ditch-card p-6 mt-8" data-testid="every-membership-card">
          <h2 className="font-heading font-bold text-[#0d2f4d]">Every membership includes</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {EVERY_MEMBERSHIP.map((e) => (
              <span key={e} className="rounded-full bg-[#f5f3ee] border border-[#e3e0d3] text-xs text-[#0d2f4d] px-3.5 py-1.5">{e}</span>
            ))}
          </div>
          <p className="text-sm text-[#51637a] mt-5 leading-relaxed">
            <span className="font-medium text-[#0d2f4d]">Additional placements:</span> if your business exceeds the successful placements included in your membership,
            additional placements are available at an exclusive member rate — always with priority service and preferred pricing over our standard recruitment fees.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-8">
          <div className="ditch-card p-7" data-testid="ditch-difference-card">
            <h2 className="font-heading font-bold text-[#0d2f4d]">The Ditch difference</h2>
            <p className="text-sm text-[#51637a] mt-3 leading-relaxed">
              Most recruitment agencies work transaction by transaction. <span className="font-medium text-[#0d2f4d]">We work relationship by relationship.</span> We
              take the time to understand your business, your culture, your leadership team and your future hiring plans — so when
              opportunities arise, we're already working on your behalf. We're not simply filling vacancies. We're helping businesses build exceptional teams.
            </p>
          </div>
          <div className="ditch-card p-7" data-testid="commitment-card">
            <h2 className="font-heading font-bold text-[#0d2f4d]">Our commitment</h2>
            <p className="text-sm text-[#51637a] mt-3 leading-relaxed">
              Every candidate we represent has been carefully screened, interviewed and matched — not only for skills and experience,
              but for cultural alignment and long-term potential. When you partner with The Ditch Careers, you're gaining more than
              a recruiter. <span className="font-medium text-[#0d2f4d]">You're gaining an extension of your business.</span>
            </p>
          </div>
        </div>

        <p className="font-accent text-xl text-[#0d2f4d] mt-10">Let's build your team together.</p>
        <p className="text-xs text-[#51637a] mt-3">Secure checkout by Stripe. Prices in AUD, billed monthly. Cancel anytime.</p>
      </div>
    </PortalLayout>
  );
}
